import {
  IonApp,
  IonContent,
  IonImg,
  IonPage,
  IonRouterOutlet,
  IonSpinner,
  IonSplitPane,
  setupIonicReact,
} from "@ionic/react";

import { useEffect, useMemo, useState } from "react";

import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import { MainMenu } from "@/modules/layout/components";
import { queryClient } from "@/api/queryClient";
import { useApiProjects } from "@/api/project.api";
import { useProjectsStore } from "@/core/stores";

// import Page from "./pages/Page";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
// import "@ionic/react/css/padding.css";
// import "@ionic/react/css/float-elements.css";
// import "@ionic/react/css/text-alignment.css";
// import "@ionic/react/css/text-transformation.css";
// import "@ionic/react/css/flex-utils.css";
// import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";
import "./theme/variables.dark.css";

import "./theme/global.css";

import {
  LocationsListPage,
  LocationsNearbyPage,
} from "@/modules/locations/pages";
import { ConfigLocationPage } from "@/modules/location/pages";

import { setDefaults as GeocodeSetDefaults, OutputFormat } from "react-geocode";
import { useJsApiLoader, LoadScriptProps } from "@react-google-maps/api";
import { AppGoogleMapsCtx } from "./modules/shared/ctxs/AppGoogleMapsCtx";
import { AboutPage } from "./modules/misc/pages";
import { LandingScreen } from "@/core/screens";

const googleMapsApiKey = "AIzaSyD5TijMxthjyjLZtRj8YUDR0V-DUsW0g3g";
const gglMapsLibs: LoadScriptProps["libraries"] = ["places"];

GeocodeSetDefaults({
  language: "he",
  outputFormat: OutputFormat.JSON,
});

setupIonicReact({
  innerHTMLTemplatesEnabled: true,
  backButtonText: "",
});

const AppContent: React.FC = () => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: googleMapsApiKey ?? "",
    region: "IL", // biased toward Israel, but not limited to it
    language: "he",
    version: "3.exp",
    libraries: gglMapsLibs,
  });

  const ctxGoogleMapsValue = useMemo(() => ({ loaded: isLoaded }), [isLoaded]);

  const { data: projects } = useApiProjects();
  const hasHydrated = useProjectsStore((state) => state._hasHydrated);
  const [isInitialized, setIsInitialized] = useState(false);

  // Sync projects to store
  useEffect(() => {
    if (projects) {
      useProjectsStore.getState().setProjects(projects);
    }
  }, [projects]);

  // Initialize current project
  useEffect(() => {
    // Wait for store rehydration before setting current project
    if (hasHydrated && projects && projects.length > 0) {
      const projectsStore = useProjectsStore.getState();
      // Only set current project if not already set from persisted state
      if (!projectsStore.currentProject) {
        // Search for project with name "מקלטים"
        const targetProject = projects.find(
          (project) => project.name.trim() === "מקלטים",
        );
        if (targetProject) {
          projectsStore.setCurrentProject(targetProject);
        } else {
          // Fallback to first project if target not found
          projectsStore.setCurrentProject(projects[0]);
        }
      }
      setIsInitialized(true);
    } else if (hasHydrated && projects && projects.length === 0) {
      // Even if no projects, mark as initialized
      setIsInitialized(true);
    }
  }, [hasHydrated, projects]);

  // Show loader while initializing
  if (!isInitialized) {
    return (
      <IonApp>
        <IonPage>
          <IonContent className="ion-padding">
            <div className="flex flex-col items-center justify-center h-full gap-8">
              <IonImg
                src="/app-logo-512.png"
                alt="App Logo"
                className="max-w-[200px] w-full h-auto"
              />
              <IonSpinner name="crescent" color="primary" />
            </div>
          </IonContent>
        </IonPage>
      </IonApp>
    );
  }

  return (
    <IonApp>
      <IonReactRouter>
        <AppGoogleMapsCtx.Provider value={ctxGoogleMapsValue}>
          <IonSplitPane contentId="main-content">
            <MainMenu />

            <IonRouterOutlet id="main-content">
              {/* <Route path="/" exact={true}>
            <Redirect to="/folder/Inbox" />
          </Route> */}

              {/* root route */}
              <Route path="/" exact={true}>
                <Redirect to="/landing" />
              </Route>

              <Route path="/landing" exact={true}>
                <LandingScreen />
              </Route>

              <Route path="/all" exact={true}>
                <LocationsListPage />
              </Route>

              <Route path="/location" exact={true}>
                <ConfigLocationPage />
              </Route>

              <Route path="/nearby" exact={true}>
                <LocationsNearbyPage />
              </Route>

              <Route path="/about" exact={true}>
                <AboutPage />
              </Route>
            </IonRouterOutlet>
          </IonSplitPane>
        </AppGoogleMapsCtx.Provider>
      </IonReactRouter>
    </IonApp>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};

export default App;
